import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, FolderOpen, BookOpen, Settings, Eye, HelpCircle, X,
  ChevronLeft, ChevronDown, ChevronRight, BarChart2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContextType';
import LanguageSelector from '@/components/LanguageSelector';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Help from '@/pages/Help';

/* ── nav items ──────────────────────────────────────────────────────────── */
type NavItem = { labelKey: string; href: string; icon: React.ReactNode };

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'navigation.home',             href: '/',                icon: <Home size={18} /> },
  { labelKey: 'navigation.projects',         href: '/projects',        icon: <FolderOpen size={18} /> },
  { labelKey: 'navigation.knowledge_bases',  href: '/knowledge-bases', icon: <BookOpen size={18} /> },
  { labelKey: 'navigation.watches',          href: '/watches',         icon: <Eye size={18} /> },
  { labelKey: 'navigation.help',             href: '/help',            icon: <HelpCircle size={18} /> },
];

/* ── helpers ─────────────────────────────────────────────────────────────── */
function getInitials(firstName: string, familyName: string): string {
  const f = firstName?.trim()[0] ?? '';
  const l = familyName?.trim()[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

/* ── component ───────────────────────────────────────────────────────────── */
const AppLayout: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(() => location.pathname.startsWith('/settings'));
  const [analyticsOpen, setAnalyticsOpen] = useState(() => location.pathname.startsWith('/analytics'));
  const [helpOpen, setHelpOpen] = useState(false);

  const sidebarW = collapsed ? 'w-14' : 'w-56';
  const initials = user ? getInitials(user.first_name, user.family_name) : '?';
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isAnalyticsActive = location.pathname.startsWith('/analytics');

  const canManageUsers = permissions.can_manage_users;
  const canManageClients = user && ['SysAdmin', 'DevOps'].includes(user.role || '');
  const isAdmin = user && ['SysAdmin', 'DevOps', 'ClientAdmin'].includes(user.role || '');
  const canViewRBAC = user && ['SysAdmin', 'DevOps'].includes(user.role || '');

  React.useEffect(() => {
    if (location.pathname.startsWith('/settings')) setSettingsOpen(true);
    if (location.pathname.startsWith('/analytics')) setAnalyticsOpen(true);
    if (location.pathname.includes('/sub-project/')) setCollapsed(true);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHelpOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [helpOpen]);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen w-full overflow-hidden bg-surface-muted font-sans">

        {/* ── Sidebar ────────────────────────────────────────────────── */}
        <aside
          className={`
            relative flex flex-col flex-shrink-0 bg-slate-50 border-r border-slate-200/60
            transition-all duration-200 ease-in-out ${sidebarW}
          `}
          style={{ boxShadow: '1px 0 0 0 rgb(226 232 240 / 0.6)' }}
        >
          {/* Brand + collapse toggle */}
          <div className={`flex items-center h-14 px-3 border-b border-slate-200/60 flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {collapsed ? (
              <button
                onClick={() => setCollapsed(false)}
                className="text-primary-700 font-bold text-sm select-none hover:text-primary-800 transition-colors"
                aria-label="Expand sidebar"
              >
                K
              </button>
            ) : (
              <>
                <span className="text-primary-700 font-semibold text-sm tracking-wide select-none">
                  KNOWLBA
                </span>
                <button
                  onClick={() => setCollapsed(true)}
                  className="flex items-center justify-center w-6 h-6 rounded bg-slate-200 hover:bg-primary-100 transition-colors flex-shrink-0 text-foreground"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft size={14} strokeWidth={3} />
                </button>
              </>
            )}
          </div>

          {/* Nav links */}
          <nav className="flex-1 overflow-y-auto py-2">
            {NAV_ITEMS.map((item) => (
              <SidebarItem
                key={item.href}
                item={item}
                collapsed={collapsed}
                label={t(item.labelKey)}
                onOpen={item.href === '/help' ? () => setHelpOpen(o => !o) : undefined}
                isOpen={item.href === '/help' ? helpOpen : undefined}
              />
            ))}

            {/* Analytics — expandable group, SysAdmin/DevOps only */}
            {canViewRBAC && (
              collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink
                      to="/analytics"
                      className={isAnalyticsActive
                        ? 'flex items-center justify-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600 pl-[10px]'
                        : 'flex items-center justify-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent pl-[10px]'}
                    >
                      <span className="flex-shrink-0"><BarChart2 size={18} /></span>
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t('navigation.analytics')}</TooltipContent>
                </Tooltip>
              ) : (
                <div>
                  <button
                    onClick={() => setAnalyticsOpen(o => !o)}
                    className={`flex items-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors border-l-2 pl-[10px] ${
                      isAnalyticsActive ? 'text-primary-700 font-medium border-primary-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-transparent'
                    }`}
                    style={{ width: 'calc(100% - 8px)' }}
                  >
                    <span className="flex-shrink-0"><BarChart2 size={18} /></span>
                    <span className="flex-1 truncate text-left">{t('navigation.analytics')}</span>
                    <span className="text-foreground">
                      {analyticsOpen ? <ChevronDown size={14} strokeWidth={2.5} /> : <ChevronRight size={14} strokeWidth={2.5} />}
                    </span>
                  </button>
                  {analyticsOpen && (
                    <div className="pl-7 pb-1 space-y-0.5">
                      <SettingsSubItem to="/analytics" label={t('pages.settings.dashboard')} />
                      <SettingsSubItem to="/analytics/costs" label={t('pages.settings.dashboardCosts')} />
                      <SettingsSubItem to="/analytics/activity" label={t('pages.settings.dashboardActivity')} />
                      {user?.role === 'SysAdmin' && <SettingsSubItem to="/admin/cost-analysis" label={t('pages.settings.costAnalysis')} />}
                    </div>
                  )}
                </div>
              )
            )}

            {/* Settings — expandable group */}
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink
                    to="/settings/general"
                    className={isSettingsActive
                      ? 'flex items-center justify-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600 pl-[10px]'
                      : 'flex items-center justify-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent pl-[10px]'}
                  >
                    <span className="flex-shrink-0"><Settings size={18} /></span>
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">{t('navigation.settings')}</TooltipContent>
              </Tooltip>
            ) : (
              <div>
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  className={`flex items-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors border-l-2 pl-[10px] ${
                    isSettingsActive ? 'text-primary-700 font-medium border-primary-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-transparent'
                  }`}
                  style={{ width: 'calc(100% - 8px)' }}
                >
                  <span className="flex-shrink-0"><Settings size={18} /></span>
                  <span className="flex-1 truncate text-left">{t('navigation.settings')}</span>
                  <span className="text-foreground">
                    {settingsOpen ? <ChevronDown size={14} strokeWidth={2.5} /> : <ChevronRight size={14} strokeWidth={2.5} />}
                  </span>
                </button>
                {settingsOpen && (
                  <div className="pl-7 pb-1 space-y-0.5">
                    <SettingsSubItem to="/settings/general" label={t('pages.settings.general')} />
                    {isAdmin && <SettingsSubItem to="/settings/billing" label={t('pages.settings.billing')} />}
                    {(isAdmin || user?.role === 'ClientAdmin') && <SettingsSubItem to="/settings/subscriptions" label={t('pages.settings.subscriptions')} />}
                    {canManageUsers && <SettingsSubItem to="/settings/users" label={t('pages.settings.users')} />}
                    {canManageClients && <SettingsSubItem to="/settings/clients" label={t('pages.settings.clients')} />}
                    {isAdmin && <SettingsSubItem to="/settings/admin" label={t('pages.settings.admin')} />}
                    {canViewRBAC && <SettingsSubItem to="/settings/rbac" label={t('pages.settings.rbac')} />}
                    {isAdmin && <SettingsSubItem to="/settings/audit-log" label={t('pages.settings.auditLog')} />}
                    {canViewRBAC && <SettingsSubItem to="/settings/metrics" label={t('pages.settings.metrics')} />}
                    {canViewRBAC && <SettingsSubItem to="/settings/embeddings" label={t('pages.settings.embeddings')} />}
                    {canViewRBAC && <SettingsSubItem to="/admin/prompts" label={t('pages.settings.prompts')} />}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Bottom section */}
          <div className="flex-shrink-0 pb-3 border-t border-slate-200/60 pt-2">

            {/* Language selector (hide when collapsed) */}
            {!collapsed && (
              <div className="px-3 py-1">
                <LanguageSelector />
              </div>
            )}

            {/* Profile avatar button */}
            {isAuthenticated && (
              <div className={`px-2 pt-1 ${collapsed ? 'flex justify-center' : ''}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate('/profile')}
                      className="
                        flex items-center justify-center w-9 h-9 rounded-full
                        bg-primary-600 text-white text-xs font-semibold
                        hover:bg-primary-700 transition-colors ring-2 ring-white
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                      "
                      aria-label="Go to profile"
                    >
                      {initials}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{user?.first_name} {user?.family_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </aside>

        {/* ── Main content ───────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* ── Help slide-over panel ───────────────────────────────── */}
      {/* Backdrop — only visible when open */}
      {helpOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => setHelpOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Panel — always mounted to preserve selected doc state; slides in/out */}
      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!helpOpen}
        aria-label={t('navigation.help')}
        className={`fixed right-0 top-0 bottom-0 z-50 w-[65vw] bg-white shadow-2xl flex flex-col transition-transform duration-200 ${helpOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-700">{t('navigation.help')}</span>
          <button
            onClick={() => setHelpOpen(false)}
            className="flex items-center justify-center w-7 h-7 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
            aria-label="Close help panel"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden min-h-0">
          <Help />
        </div>
      </div>
    </TooltipProvider>
  );
};

/* ── SidebarItem ─────────────────────────────────────────────────────────── */
interface SidebarItemProps {
  item: NavItem;
  collapsed: boolean;
  label: string;
  onOpen?: () => void;
  isOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ item, collapsed, label, onOpen, isOpen }) => {
  const content = onOpen ? (
    <button
      onClick={onOpen}
      className={`
        flex items-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors w-full
        ${collapsed ? 'justify-center' : ''}
        ${isOpen
          ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600 pl-[10px]'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent pl-[10px]'}
      `}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  ) : (
    <NavLink
      to={item.href}
      end={item.href === '/'}
      className={({ isActive }) => `
        flex items-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-colors
        ${collapsed ? 'justify-center' : ''}
        ${isActive
          ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600 pl-[10px]'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent pl-[10px]'}
      `}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }
  return content;
};

/* ── SettingsSubItem ─────────────────────────────────────────────────────── */
const SettingsSubItem: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `block px-3 py-1.5 rounded-md text-sm transition-colors ${
        isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      }`
    }
  >
    {label}
  </NavLink>
);

export default AppLayout;
